import React from "react";
import _ from "lodash";
import AdvertiserAndBrandsSelector from "../custom-upr/AdvertiserAndBrandsSelector";
import TextAreaWithJsonCheck from "components/common/TextAreaWithJsonCheck";
import ClickToCopyWrapper from "components/common/ClickToCopyWrapper";
import RadioControl from "components/common/RadioControl";

import { BRANDED } from "./Constants";

const inputClass =
  "bg-white flex border border-gray-400 rounded py-1 px-3 appearance-none leading-normal focus:outline-none focus:border-blue-400 focus:shadow-inner hover:border-gray-500";

const PRICE_TYPE_NAME = {
  FLOOR: 0,
  TARGET_CPM: 1,
};

class PriceSettingControl extends React.PureComponent {
  constructor(props) {
    super(props);

    const { currentSetting } = props;
    const {
      price = "",
      priceType = "",
      currency = props.currency || "",
      isSetForEverything = false,
      branded = BRANDED.ALLOWED,
      targeting = {},
    } = currentSetting;

    this.state = {
      price,
      priceType,
      targeting,
      currency,
      isSetForEverything,
      branded,

      errMsg: null,
    };

    this.handlePriceTypeChanged = this.handlePriceTypeChanged.bind(this);
    this.handlePriceChanged = this.handlePriceChanged.bind(this);
    this.handleIsSetForEverythingChanged =
      this.handleIsSetForEverythingChanged.bind(this);
    this.handleBrandedChanged = this.handleBrandedChanged.bind(this);
    this.handleAdvertiserAndBrandsChanged =
      this.handleAdvertiserAndBrandsChanged.bind(this);
    this.handleAddPriceSetting = this.handleAddPriceSetting.bind(this);
  }

  handlePriceTypeChanged(priceType) {
    this.setState({ priceType });
  }

  handlePriceChanged(e) {
    const price = e.target.value ? parseFloat(e.target.value) : "";
    this.setState({ price });
  }

  handleIsSetForEverythingChanged(isSetForEverything) {
    this.setState({ isSetForEverything });
  }

  handleBrandedChanged(branded) {
    if (branded === BRANDED.ALLOWED) {
      this.setState({ currency: this.props.currency });
    }
    this.setState({ branded });
  }

  handleAdvertiserAndBrandsChanged(selectedOptions) {
    if (!selectedOptions || _.isEmpty(selectedOptions)) {
      const newTargeting = _.omit(this.state.targeting, "advertisersAndBrands");
      this.setState({ targeting: newTargeting });
      return;
    }

    let optionsByAdvertiser = _(selectedOptions)
      .groupBy("advertiserId")
      .reduce((result, options) => {
        const advertiserId = _.first(options).advertiserId;
        let brandIds = [];
        _.forEach(options, (option) => {
          if (option.type === "BRAND") {
            brandIds.push(option.value);
          }
        });
        result.push({
          advertiserId,
          brandIds,
        });
        return result;
      }, []);
    const targeting = this.state.targeting;
    const newTargeting = {
      ...targeting,
      advertisersAndBrands: optionsByAdvertiser,
    };
    this.setState({ targeting: newTargeting });
  }

  handleAddPriceSetting() {
    const {
      priceType,
      price,
      currency,
      isSetForEverything,
      branded,
      targeting,
    } = this.state;

    // validate
    if (priceType === null || priceType === undefined || priceType === "") {
      return this.setState({ errMsg: "Price Type is required" });
    }

    if (price === null || price === undefined || price === "") {
      return this.setState({ errMsg: "Price is required" });
    }

    if (currency === "" && branded === BRANDED.ALLOWED) {
      return this.setState({ errMsg: "Currency is required" });
    }

    // advertisersAndBrands max is 49
    const advertisersAndBrands = _.get(targeting, "advertisersAndBrands", []);
    if (advertisersAndBrands.length > 49) {
      return this.setState({
        errMsg: "Cannot have more than 49 Advertiser and Brands Targeting",
      });
    }

    if (!isSetForEverything && advertisersAndBrands.length === 0) {
      return this.setState({
        errMsg:
          "Advertisers and Brands Targeting is required when isSetForEverything is false",
      });
    }

    this.setState({ errMsg: null });
    const newSetting = {
      price,
      priceType,
      currency,
      isSetForEverything,
      branded,
    };

    if (targeting["advertisersAndBrands"] && !isSetForEverything) {
      newSetting.targeting = targeting;
    }

    this.props.handleAddSetting(newSetting);
  }

  render() {
    const { networkId, handleCancel, isSetForEverythingExist } = this.props;
    const {
      price,
      priceType,
      currency,
      targeting,
      isSetForEverything,
      branded,
      errMsg,
    } = this.state;

    return (
      <div className="rounded bg-white p-2 text-sm">
        <div className="border-b mb-4 text-base">New Price Setting</div>

        {/* Price Type */}
        <div className="mb-2">
          <div className="block font-bold text-gray-800">Price Type</div>
          <RadioControl
            options={_.values(PRICE_TYPE_NAME).map((d) => ({
              value: d,
              label: d === PRICE_TYPE_NAME.FLOOR ? "Floor" : "Target CPM",
            }))}
            value={priceType}
            onChange={(value) =>
              this.handlePriceTypeChanged(value, "priceType")
            }
          ></RadioControl>
        </div>

        {/* Price */}
        <div className="mb-2">
          <label className="block font-bold text-gray-800" htmlFor="price">
            Price
          </label>
          <input
            className={inputClass + " w-64"}
            id="price"
            type="number"
            value={price}
            step={0.01}
            min={0}
            onChange={this.handlePriceChanged}
          />
        </div>

        {/* Currency */}
        <div className="mb-2">
          <label className="block font-bold text-gray-800" htmlFor="currency">
            Currency
          </label>
          <div>{currency}</div>
        </div>

        {/* isSetForEverything */}
        <div className="mb-2">
          <label
            className="block font-bold text-gray-800"
            htmlFor="isSetForEverything"
          >
            Is set for everything
          </label>
          <RadioControl
            options={[true, false].map((d) => ({
              value: d,
              label: d === true ? "True" : "False",
              disabled: isSetForEverythingExist,
            }))}
            value={isSetForEverything}
            onChange={(value) => this.handleIsSetForEverythingChanged(value)}
          ></RadioControl>
          {isSetForEverythingExist && (
            <div className="text-xs text-orange-600">
              Only one item can have isSetForEverything set to true.
            </div>
          )}
        </div>

        {/* Branded */}
        <div className="mb-2">
          <label className="block font-bold text-gray-800" htmlFor="branded">
            Branded
          </label>
          <RadioControl
            options={_.values(BRANDED).map((d) => ({
              value: d,
              label: d === BRANDED.ALLOWED ? "Allowed" : "Blocked",
            }))}
            value={branded}
            onChange={(value) => this.handleBrandedChanged(value)}
          ></RadioControl>
        </div>

        {/* Advertisers and Brands */}
        {!isSetForEverything && (
          <div className="mb-2">
            <label className="block font-bold text-gray-800">
              Advertisers and Brands Targeting
            </label>
            <AdvertiserAndBrandsControl
              networkId={networkId}
              targeting={targeting["advertisersAndBrands"]}
              handleChanged={this.handleAdvertiserAndBrandsChanged}
            ></AdvertiserAndBrandsControl>
          </div>
        )}

        <div className="border-t mb-2 mt-1 flex items-center justify-end px-2 py-2">
          <div className="text-red-600">{errMsg}</div>
          <button
            type="button"
            className="px-4 py-1 text-blue-700 hover:underline"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded shadow bg-blue-200 px-4 py-1 font-semibold text-blue-700 hover:bg-blue-300"
            onClick={this.handleAddPriceSetting}
          >
            Add Price Setting
          </button>
        </div>
      </div>
    );
  }
}

const DEFAULT_ADVERTISERS_AND_BRANDS = [
  {
    advertiserId: "0",
    brandIds: [],
  },
  {
    advertiserId: "111",
    brandIds: ["222", "333"],
  },
];
class AdvertiserAndBrandsControl extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      controller: props.targeting ? "includes" : "NONE", // "NONE", "includes"
      controlType: "JSON", // "SELECTOR"
      jsonString: props.targeting ? JSON.stringify(props.targeting) : "",

      finalTargeting: null,
      verifyErrMsg: null,
    };

    this.handleControllerChanged = this.handleControllerChanged.bind(this);
    this.handleControlTypeChanged = this.handleControlTypeChanged.bind(this);
    this.handleInputChanged = this.handleInputChanged.bind(this);
    this.handleVerify = this.handleVerify.bind(this);
    this.handleClearAdvertisersAndBrands =
      this.handleClearAdvertisersAndBrands.bind(this);
  }

  // when control type changes, update jsonString or selector to the same value
  componentDidUpdate(prevProps) {
    if (prevProps.targeting !== this.props.targeting) {
      this.setState({
        jsonString: this.props.targeting
          ? JSON.stringify(this.props.targeting)
          : "",
      });
    }
  }

  handleInputChanged(inputValue) {
    this.setState({ jsonString: inputValue });
  }

  handleControllerChanged(controller) {
    this.setState({
      controller,
    });

    if (controller === "NONE") {
      this.handleClearAdvertisersAndBrands();
    }
  }

  handleControlTypeChanged(type) {
    this.setState({
      controlType: type,
      finalTargeting: null,
      verifyErrMsg: null,
    });
  }

  handleVerify() {
    this.setState({ finalTargeting: null });

    setTimeout(() => {
      try {
        this.setState({
          finalTargeting: JSON.parse(this.state.jsonString),
          verifyErrMsg: null,
        });
        this.props.handleChanged(this.state.finalTargeting);
      } catch (err) {
        this.setState({ verifyErrMsg: err.toString() });
      }
    });
  }

  handleClearAdvertisersAndBrands() {
    this.setState({ jsonString: "" });
    this.props.handleChanged(null);
  }

  render() {
    const { networkId, targeting, handleChanged } = this.props;
    const {
      controller,
      controlType,
      jsonString,
      finalTargeting,
      verifyErrMsg,
    } = this.state;
    const defaultValue = JSON.stringify(DEFAULT_ADVERTISERS_AND_BRANDS);

    return (
      <>
        <div className="flex items-center gap-4">
          <label
            htmlFor={`radio_advb_controller_none`}
            className="cursor-pointer"
          >
            <input
              id={`radio_advb_controller_none`}
              type="radio"
              name={`radio_advb_controller`}
              value={"NONE"}
              checked={controller === "NONE"}
              onChange={() => this.handleControllerChanged("NONE")}
            />{" "}
            Any Advertiser or Brand
          </label>

          <label
            htmlFor={`radio_advb_controller_includes`}
            className="cursor-pointer"
          >
            <input
              id={`radio_advb_controller_includes`}
              type="radio"
              name={`radio_advb_controller`}
              value={"includes"}
              checked={controller === "includes"}
              onChange={() => this.handleControllerChanged("includes")}
            />{" "}
            Includes
          </label>
        </div>
        {controller === "includes" && (
          <div className="border mt-1 px-2 py-1">
            <div className="flex items-center gap-4">
              <label
                htmlFor={`radio_advb_control_type_json`}
                className="cursor-pointer"
              >
                <input
                  id={`radio_advb_control_type_json`}
                  type="radio"
                  name={`radio_advb_control_type`}
                  value={"JSON"}
                  checked={controlType === "JSON"}
                  onChange={() => this.handleControlTypeChanged("JSON")}
                />{" "}
                JSON
              </label>

              <label
                htmlFor={`radio_advb_control_type_selector`}
                className="cursor-pointer"
              >
                <input
                  id={`radio_advb_control_type_selector`}
                  type="radio"
                  name={`radio_advb_control_type`}
                  value={"SELECTOR"}
                  checked={controlType === "SELECTOR"}
                  onChange={() => this.handleControlTypeChanged("SELECTOR")}
                />{" "}
                Selector
              </label>
            </div>
            {controlType === "JSON" ? (
              <>
                <div className="flex items-center gap-1 text-xs text-gray-700">
                  <div>ex. {defaultValue}</div>
                  <ClickToCopyWrapper
                    copyText={defaultValue}
                  ></ClickToCopyWrapper>
                </div>
                <TextAreaWithJsonCheck
                  rows={6}
                  inputValue={jsonString}
                  handleInputChanged={this.handleInputChanged}
                ></TextAreaWithJsonCheck>

                <div className="items flex gap-2 pt-2">
                  <button
                    type="button"
                    className="rounded shadow mb-2 bg-gray-200 px-4 py-1 font-semibold text-gray-700 hover:bg-gray-300"
                    onClick={this.handleClearAdvertisersAndBrands}
                  >
                    Clear advertisers and brands
                  </button>
                  <button
                    type="button"
                    className="rounded shadow mb-2 bg-teal-200 px-4 py-1 font-semibold text-teal-700 hover:bg-teal-300"
                    onClick={this.handleVerify}
                  >
                    Verify Advertisers and Brands JSON
                  </button>
                </div>
                <div className="pt-2">
                  {verifyErrMsg && (
                    <div className="text-red-700">{verifyErrMsg}</div>
                  )}
                  {finalTargeting && (
                    <AdvertiserAndBrandsSelector
                      networkId={networkId}
                      targeting={finalTargeting}
                      isDisabled={true}
                      // handleTargetingChanged={handleChanged}
                    ></AdvertiserAndBrandsSelector>
                  )}
                </div>
              </>
            ) : (
              <AdvertiserAndBrandsSelector
                networkId={networkId}
                targeting={targeting}
                handleTargetingChanged={handleChanged}
              ></AdvertiserAndBrandsSelector>
            )}
          </div>
        )}
      </>
    );
  }
}

export default PriceSettingControl;
